import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Eye,
  ExternalLink,
  Hash,
  Link2,
  Link2Off,
  Loader,
  MessagesSquare,
  RefreshCw,
  ShieldAlert,
  ShieldOff,
  UserPlus,
  Users,
} from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { ConfirmationModal } from '../ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { PartnerAvatar } from './PartnerAvatar';
import { PartnerSyncStatus } from './PartnerSyncStatus';
import {
  PARTNER_TIERS,
  type PartnerDetail,
  type PartnerDiscord,
  type PartnerDiscordHealth,
  type PartnerSyncResult,
  type PartnerTier,
  type PartnerUnmanagedPerson,
  type SyncOutcome,
} from '../../types/partners';
import { cn } from '../../utils/cn';
import { discordChannelUrl, discordName, isDiscordId, memberName } from '../../utils/partnerLinks';
import { tierLabel } from '../../utils/partnerStatus';
import {
  channelSlug,
  describeSyncOutcome,
  planHeadline,
  planLines,
  type NameOf,
  type PartnerNotice,
} from '../../utils/partnerSync';

interface PartnerDiscordSectionProps {
  detail: PartnerDetail;
  canEdit: boolean;
  /** PR admin: may apply role removals the bot held back. */
  canAdmin: boolean;
  /** The page's clock, for relative times. */
  now: number;
  onChanged: () => void | Promise<void>;
  onNotice: (notice: PartnerNotice) => void;
}

type Busy = null | 'managed' | 'early_access' | 'strict_access' | 'sync' | 'preview' | 'force' | 'link' | 'unlink' | `person:${string}`;

interface LastResult {
  result: PartnerSyncResult;
  at: number;
}

interface EnableFlow {
  loading: boolean;
  outcome: SyncOutcome | null;
  error: string | null;
}

const Toggle = ({
  label,
  hint,
  checked,
  disabled,
  busy,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
}) => (
  <label className={cn('flex items-start justify-between gap-4 py-3', disabled && 'opacity-70')}>
    <span className="min-w-0">
      <span className="block text-sm font-medium text-white">{label}</span>
      <span className="block text-xs text-glass-400 mt-0.5">{hint}</span>
    </span>
    <span className="flex items-center gap-2 shrink-0 pt-0.5">
      {busy && <Loader className="w-4 h-4 animate-spin text-glass-400" />}
      <span className="switch">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
        <span />
      </span>
    </span>
  </label>
);

/**
 * How Mr. Roboto manages this partner on Discord: the three switches, the
 * channel link, previews and syncs, and the people who have channel access
 * without being on the team. Nothing here changes Discord without saying
 * what it will do first.
 */
export const PartnerDiscordSection = ({ detail, canEdit, canAdmin, now, onChanged, onNotice }: PartnerDiscordSectionProps) => {
  const { creator, discord } = detail;
  const [busy, setBusy] = useState<Busy>(null);
  const [last, setLast] = useState<LastResult | null>(null);
  const [enable, setEnable] = useState<EnableFlow | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [confirmStrict, setConfirmStrict] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);
  const [unlink, setUnlink] = useState<{ recreate: boolean } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PartnerUnmanagedPerson | null>(null);
  const [addTiers, setAddTiers] = useState<Record<string, PartnerTier>>({});

  const [linking, setLinking] = useState(false);
  const [health, setHealth] = useState<PartnerDiscordHealth | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [linkChoice, setLinkChoice] = useState('');
  const [linkPasted, setLinkPasted] = useState('');

  const channelName = discord.observed_detail?.name ?? last?.result.observed?.channel.name ?? null;
  const previewName = channelName ?? channelSlug(creator.name);
  const openUrl = discordChannelUrl(discord.guild_id, discord.channel_id);

  const nameOf: NameOf = useMemo(() => {
    const names = new Map<string, string>();
    for (const m of detail.members) names.set(m.discord_id, memberName(m));
    for (const p of last?.result.unmanaged ?? []) names.set(p.discord_id, discordName(p, p.discord_id));
    for (const m of last?.result.observed?.members ?? []) if (!names.has(m.discord_id)) names.set(m.discord_id, discordName(m, m.discord_id));
    return (id: string) => names.get(id) ?? id;
  }, [detail.members, last]);

  const activeIds = useMemo(() => new Set(detail.members.filter((m) => m.status === 'active').map((m) => m.discord_id)), [detail.members]);
  const unmanaged = (last?.result.unmanaged ?? []).filter((p) => !activeIds.has(p.discord_id));
  const held = last?.result.plan.roles.held ?? [];
  const heldMembers = detail.members.filter((m) => m.access_state === 'held').length;
  const unmanagedCount = last ? unmanaged.length : discord.observed_detail?.unmanaged_count ?? 0;

  const fail = (error: unknown, fallback: string) => onNotice({ tone: 'danger', text: error instanceof Error ? error.message : fallback });

  const saveFlags = async (patch: Partial<Pick<PartnerDiscord, 'managed' | 'early_access' | 'strict_access'>>, saved: string, key: Busy) => {
    setBusy(key);
    try {
      const res = await partnersApi.updateDiscord(creator.id, patch);
      if (res.sync?.ok) setLast({ result: res.sync.result, at: Date.now() });
      onNotice(describeSyncOutcome(res.sync, saved, res.discord.managed));
      await onChanged();
    } catch (error) {
      fail(error, 'The setting could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  // ── Managed ────────────────────────────────────────────────────────────

  const startEnable = async () => {
    setEnable({ loading: true, outcome: null, error: null });
    try {
      const outcome = await partnersApi.sync(creator.id, { dry_run: true });
      setEnable({ loading: false, outcome, error: null });
      if (outcome.ok) setLast({ result: outcome.result, at: Date.now() });
    } catch (error) {
      setEnable({ loading: false, outcome: null, error: error instanceof Error ? error.message : 'Mr. Roboto could not preview this partner.' });
    }
  };

  const confirmEnable = async () => {
    await saveFlags({ managed: true }, `Turned on Discord management for ${creator.name}.`, 'managed');
    setEnable(null);
  };

  const onManagedChange = (next: boolean) => {
    if (next) void startEnable();
    else setConfirmOff(true);
  };

  // ── Sync and preview ───────────────────────────────────────────────────

  const runSync = async (options: { dry_run?: boolean; force?: boolean }, key: Busy) => {
    setBusy(key);
    try {
      const outcome = await partnersApi.sync(creator.id, options);
      if (outcome.ok) setLast({ result: outcome.result, at: Date.now() });
      if (options.dry_run) {
        onNotice(
          outcome.ok
            ? { tone: 'info', text: `Preview: ${planHeadline(outcome.result.plan, previewName)} Nothing has changed in Discord.` }
            : { tone: 'warning', text: `Mr. Roboto could not preview this partner. ${outcome.error}` },
        );
      } else {
        onNotice(
          describeSyncOutcome(
            outcome,
            options.force ? 'Asked Mr. Roboto to apply the held role removals.' : 'Asked Mr. Roboto to sync this partner now.',
          ),
        );
        await onChanged();
      }
    } catch (error) {
      fail(error, 'Mr. Roboto could not be asked.');
    } finally {
      setBusy(null);
    }
  };

  // ── Channel link ───────────────────────────────────────────────────────

  const openLink = async () => {
    setLinking(true);
    setLinkChoice('');
    setLinkPasted('');
    setHealthLoading(true);
    setHealthError(null);
    try {
      setHealth(await partnersApi.discordHealth());
    } catch (error) {
      setHealthError(error instanceof Error ? error.message : 'Mr. Roboto could not list the channels.');
    } finally {
      setHealthLoading(false);
    }
  };

  const linkTarget = linkChoice || (isDiscordId(linkPasted.trim()) ? linkPasted.trim() : '');

  const doLink = async () => {
    if (!linkTarget) return;
    setBusy('link');
    try {
      const res = await partnersApi.linkChannel(creator.id, linkTarget);
      const picked = health?.unlinked_channels.find((c) => c.id === linkTarget);
      onNotice(
        describeSyncOutcome(
          res.sync,
          `Linked ${picked ? `#${picked.name}` : 'the channel'} to ${creator.name}. ${
            res.discord.managed ? '' : 'Preview it next, then import the people who already have access and turn management on.'
          }`,
          res.discord.managed,
        ),
      );
      setLinking(false);
      await onChanged();
    } catch (error) {
      fail(error, 'The channel could not be linked.');
    } finally {
      setBusy(null);
    }
  };

  const doUnlink = async () => {
    if (!unlink) return;
    setBusy('unlink');
    try {
      const res = await partnersApi.unlinkChannel(creator.id, unlink.recreate);
      onNotice(
        describeSyncOutcome(
          res.sync,
          unlink.recreate
            ? 'Unlinked the channel. Mr. Roboto creates a new one on its next pass.'
            : 'Unlinked the channel and turned off Discord management. The old channel stays in Discord as it is.',
          res.discord.managed,
        ),
      );
      setLast(null);
      await onChanged();
    } catch (error) {
      fail(error, 'The channel could not be unlinked.');
    } finally {
      setBusy(null);
      setUnlink(null);
    }
  };

  // ── People with access who are not on the team ─────────────────────────

  const addPerson = async (person: PartnerUnmanagedPerson) => {
    const tier = addTiers[person.discord_id] ?? 'contributor';
    setBusy(`person:${person.discord_id}`);
    try {
      const res = await partnersApi.addMember(creator.id, { discord_id: person.discord_id, tier });
      onNotice(describeSyncOutcome(res.sync, `Added ${discordName(person, person.discord_id)} to the team as a ${tierLabel(tier).label}.`, discord.managed));
      setLast((prev) => prev && { ...prev, result: { ...prev.result, unmanaged: prev.result.unmanaged.filter((p) => p.discord_id !== person.discord_id) } });
      await onChanged();
    } catch (error) {
      fail(error, 'They could not be added.');
    } finally {
      setBusy(null);
    }
  };

  const revokePerson = async () => {
    if (!revokeTarget) return;
    const person = revokeTarget;
    setBusy(`person:${person.discord_id}`);
    try {
      const res = await partnersApi.revokeAccess(creator.id, person.discord_id);
      onNotice(describeSyncOutcome(res.sync, `Marked ${discordName(person, person.discord_id)} for access removal.`, discord.managed));
      setLast((prev) => prev && { ...prev, result: { ...prev.result, unmanaged: prev.result.unmanaged.filter((p) => p.discord_id !== person.discord_id) } });
      await onChanged();
    } catch (error) {
      fail(error, 'Their access could not be removed.');
    } finally {
      setBusy(null);
      setRevokeTarget(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const disabled = !canEdit || busy !== null;
  const enableOutcome = enable?.outcome;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Discord"
        icon={MessagesSquare}
        actions={
          <>
            {openUrl && (
              <a href={openUrl} target="_blank" rel="noopener noreferrer" className="glass-button-secondary">
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">Open in Discord</span>
              </a>
            )}
            {canEdit && (
              <button type="button" className="glass-button" onClick={() => void runSync({ dry_run: true }, 'preview')} disabled={busy !== null} title="See what a sync would change, without changing anything">
                {busy === 'preview' ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Preview
              </button>
            )}
            {canEdit && discord.managed && (
              <button type="button" className="btn-primary" onClick={() => void runSync({}, 'sync')} disabled={busy !== null}>
                {busy === 'sync' ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Sync now
              </button>
            )}
          </>
        }
      />
      <p className="text-sm text-glass-400">
        When Discord is managed, Mr. Roboto keeps this partner’s private channel, channel access and partner roles matching the team here: it lets in
        people you add, takes access from people you remove, and archives the channel when every partnership has ended.
      </p>

      <PartnerSyncStatus discord={discord} now={now} />

      <div className="subcard px-3 md:px-4 divide-y divide-glass-200/10" data-tour="partner-discord-managed">
        <Toggle
          label="Managed by Mr. Roboto"
          hint="Turning this on shows what Mr. Roboto would change first, so nothing happens by surprise."
          checked={discord.managed}
          disabled={disabled}
          busy={busy === 'managed'}
          onChange={onManagedChange}
        />
        <Toggle
          label="Open the channel early"
          hint="Give the team the channel before any partnership is active. Roles still wait until a partnership is active."
          checked={discord.early_access}
          disabled={disabled}
          busy={busy === 'early_access'}
          onChange={(next) =>
            void saveFlags({ early_access: next }, next ? 'The channel opens early for this team.' : 'The channel no longer opens early.', 'early_access')
          }
        />
        <Toggle
          label="Only the team may access the channel"
          hint="Mr. Roboto also takes access from people who were let in by hand but are not on the team. Access through staff roles is never touched."
          checked={discord.strict_access}
          disabled={disabled}
          busy={busy === 'strict_access'}
          onChange={(next) =>
            next ? setConfirmStrict(true) : void saveFlags({ strict_access: false }, 'People let in by hand keep their channel access again.', 'strict_access')
          }
        />
      </div>

      <div className="subcard p-3 md:p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="eyebrow mb-1">Channel</p>
            {discord.channel_id ? (
              <>
                <p className="text-sm text-white flex items-center gap-1.5">
                  <Hash className="w-4 h-4 text-glass-400" />
                  <span className="font-mono">{channelName ?? discord.channel_id}</span>
                  {discord.observed_detail?.category_name && <span className="text-glass-400">in {discord.observed_detail.category_name}</span>}
                </p>
                <p className="text-xs text-glass-500 mt-0.5">
                  {discord.channel_origin === 'created' ? 'Created by Mr. Roboto.' : 'Linked by staff.'} Channel ID{' '}
                  <span className="font-mono">{discord.channel_id}</span>.
                </p>
              </>
            ) : (
              <p className="text-sm text-glass-300">
                No channel is linked. When management is on and a partnership is active (or the channel is open early), Mr. Roboto creates{' '}
                <span className="font-mono">#{previewName}</span> in the first partner category with room. If the partner already has a hand-made
                channel, link it instead.
              </p>
            )}
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              {discord.channel_id ? (
                <button type="button" className="btn-danger" onClick={() => setUnlink({ recreate: false })} disabled={busy !== null}>
                  <Link2Off className="w-4 h-4" />
                  Unlink
                </button>
              ) : (
                !linking && (
                  <button type="button" className="glass-button" onClick={() => void openLink()} disabled={busy !== null}>
                    <Link2 className="w-4 h-4" />
                    Link existing channel
                  </button>
                )
              )}
            </div>
          )}
        </div>

        {linking && !discord.channel_id && (
          <div className="space-y-2 border-t border-glass-200/10 pt-3">
            {healthLoading ? (
              <p className="text-xs text-glass-400 flex items-center gap-2">
                <Loader className="w-3.5 h-3.5 animate-spin" />
                Asking Mr. Roboto which partner channels are not linked yet…
              </p>
            ) : healthError ? (
              <div className="alert alert-warning">
                <AlertTriangle className="w-4 h-4" />
                <span>{healthError} You can still paste the channel ID below.</span>
              </div>
            ) : (
              <div>
                <label htmlFor="partner-link-channel" className="block text-xs font-medium text-glass-400 mb-1.5">
                  Channels in the partner categories that no partner has yet
                </label>
                <select id="partner-link-channel" className="glass-select w-full" value={linkChoice} onChange={(e) => setLinkChoice(e.target.value)}>
                  <option value="">{health?.unlinked_channels.length ? 'Choose a channel…' : 'Every partner channel is already linked'}</option>
                  {health?.unlinked_channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      #{c.name}
                      {c.category_name ? ` (${c.category_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!linkChoice && (
              <div>
                <label htmlFor="partner-link-paste" className="block text-xs font-medium text-glass-400 mb-1.5">
                  Or paste a channel ID
                </label>
                <input
                  id="partner-link-paste"
                  className="glass-input w-full font-mono"
                  inputMode="numeric"
                  placeholder="123456789012345678"
                  value={linkPasted}
                  onChange={(e) => setLinkPasted(e.target.value)}
                />
                {linkPasted.trim() && !isDiscordId(linkPasted.trim()) && <p className="text-xs text-red-400 mt-1">A channel ID is 17 to 20 digits. Right-click the channel in Discord and choose Copy Channel ID.</p>}
              </div>
            )}
            <p className="text-xs text-glass-500">
              Linking changes nothing in Discord by itself. Preview next to see who already has access, add them to the team with one click each,
              then turn management on.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="glass-button-secondary" onClick={() => setLinking(false)} disabled={busy === 'link'}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={() => void doLink()} disabled={!linkTarget || busy !== null}>
                {busy === 'link' ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Link channel
              </button>
            </div>
          </div>
        )}
      </div>

      {last && (
        <div className="subcard p-3 md:p-4 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="eyebrow">{last.result.dry_run ? 'Preview' : 'Last sync'}</p>
            <span className="text-xs text-glass-500 tabular-nums">{formatDistanceToNow(new Date(last.at), { addSuffix: true })}</span>
          </div>
          <p className="text-sm font-medium text-white">{planHeadline(last.result.plan, previewName)}</p>
          {planLines(last.result.plan, nameOf, previewName).length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-sm text-glass-300">
              {planLines(last.result.plan, nameOf, previewName).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {last.result.dry_run && <p className="text-xs text-glass-500">This was a preview. Nothing has changed in Discord.</p>}
        </div>
      )}

      {(held.length > 0 || heldMembers > 0) && (
        <div className="alert alert-warning">
          <ShieldAlert className="w-4 h-4" />
          <div className="flex-1 min-w-0 space-y-1">
            <p>
              {held.length > 0
                ? `${held.length} role removal${held.length === 1 ? ' is' : 's are'} held back.`
                : `${heldMembers} ${heldMembers === 1 ? 'person has' : 'people have'} a removal held back.`}{' '}
              Mr. Roboto holds removals in setup mode, and when one pass would remove more than its safety cap allows.
            </p>
            {!canAdmin && <p className="text-xs">A PR admin can apply them from here.</p>}
          </div>
          {canAdmin && discord.managed && (
            <button type="button" className="btn-danger btn-xs shrink-0" onClick={() => setConfirmForce(true)} disabled={busy !== null}>
              {busy === 'force' ? <Loader className="w-3.5 h-3.5 animate-spin" /> : null}
              Apply held removals
            </button>
          )}
        </div>
      )}

      <div className="subcard p-3 md:p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-accent-300" />
          <h4 className="text-sm font-semibold text-white">People with access who aren’t on the team</h4>
          {last && <span className="badge badge-neutral">{unmanaged.length}</span>}
        </div>
        <p className="text-xs text-glass-400">
          They can see the channel but are not on the team, usually because they were let in by hand before Argus managed it. Add each to the team,
          or remove their access.
        </p>
        {!last ? (
          <p className="text-sm text-glass-400">
            {unmanagedCount > 0
              ? `Mr. Roboto last counted ${unmanagedCount} ${unmanagedCount === 1 ? 'person' : 'people'} like this. Run a preview to see who.`
              : discord.channel_id
                ? 'Run a preview to see who else can see the channel.'
                : 'Link a channel first. Its current members show up here after a preview.'}
          </p>
        ) : unmanaged.length === 0 ? (
          <p className="text-sm text-glass-400">Nobody. Everyone with access to the channel is on the team.</p>
        ) : (
          <ul className="divide-y divide-glass-200/10">
            {unmanaged.map((person) => {
              const name = discordName(person, person.discord_id);
              const rowBusy = busy === `person:${person.discord_id}`;
              return (
                <li key={person.discord_id} className="py-2.5 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2.5 min-w-[12rem] flex-1">
                    <PartnerAvatar avatarUrl={null} name={name} size="md" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{name === person.discord_id ? 'Discord member' : name}</p>
                      <p className="text-[11px] font-mono text-glass-500 truncate">
                        {person.username ? `@${person.username} · ` : ''}
                        {person.discord_id}
                      </p>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        className="glass-select min-h-[32px]"
                        value={addTiers[person.discord_id] ?? 'contributor'}
                        onChange={(e) => setAddTiers((prev) => ({ ...prev, [person.discord_id]: e.target.value as PartnerTier }))}
                        aria-label={`Tier for ${name}`}
                        disabled={rowBusy}
                      >
                        {PARTNER_TIERS.map((tier) => (
                          <option key={tier} value={tier}>
                            {tierLabel(tier).label}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="glass-button glass-button-sm" onClick={() => void addPerson(person)} disabled={busy !== null}>
                        {rowBusy ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                        Add to team
                      </button>
                      <button type="button" className="btn-danger glass-button-sm" onClick={() => setRevokeTarget(person)} disabled={busy !== null}>
                        <ShieldOff className="w-4 h-4" />
                        Remove access
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!enable}
        onClose={() => busy !== 'managed' && setEnable(null)}
        onConfirm={() => void confirmEnable()}
        tone="accent"
        title={`Let Mr. Roboto manage ${creator.name} on Discord?`}
        message="From now on Mr. Roboto keeps the channel, channel access and partner roles matching this team. This is what it would do right now."
        confirmText="Turn on management"
        confirmDisabled={!!enable?.loading}
        isLoading={busy === 'managed'}
      >
        {enable?.loading ? (
          <p className="text-sm text-glass-400 flex items-center gap-2">
            <Loader className="w-4 h-4 animate-spin" />
            Asking Mr. Roboto for a preview…
          </p>
        ) : enableOutcome?.ok ? (
          <div className="subcard p-3 space-y-2">
            <p className="text-sm font-medium text-white">{planHeadline(enableOutcome.result.plan, previewName)}</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-glass-300">
              {planLines(enableOutcome.result.plan, nameOf, previewName).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {enableOutcome.result.unmanaged.length > 0 && (
              <p className="text-xs text-glass-400">
                {enableOutcome.result.unmanaged.length} {enableOutcome.result.unmanaged.length === 1 ? 'person has' : 'people have'} access without being on
                the team. They keep it unless strict access is on; you can add them to the team below afterwards.
              </p>
            )}
          </div>
        ) : (
          <div className="alert alert-warning">
            <AlertTriangle className="w-4 h-4" />
            <span>
              Mr. Roboto could not preview this partner: {enableOutcome && !enableOutcome.ok ? enableOutcome.error : enable?.error}. You can still turn
              management on. Mr. Roboto then applies the team on its next pass, and this page shows what it did.
            </span>
          </div>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={confirmOff}
        onClose={() => setConfirmOff(false)}
        onConfirm={() => {
          setConfirmOff(false);
          void saveFlags({ managed: false }, `Turned off Discord management for ${creator.name}. Discord stays as it is now.`, 'managed');
        }}
        title={`Stop managing ${creator.name} on Discord?`}
        message="Mr. Roboto stops changing this partner’s channel, access and roles. Everything in Discord stays exactly as it is now: nobody gains or loses access until management is back on."
        confirmText="Stop managing"
      />

      <ConfirmationModal
        isOpen={confirmStrict}
        onClose={() => setConfirmStrict(false)}
        onConfirm={() => {
          setConfirmStrict(false);
          void saveFlags({ strict_access: true }, 'Only the team may access the channel now.', 'strict_access');
        }}
        title="Only let the team into the channel?"
        message={`Mr. Roboto takes channel access away from anyone let in by hand who is not on the team${
          unmanagedCount > 0 ? ` (${unmanagedCount} ${unmanagedCount === 1 ? 'person' : 'people'} at the last count)` : ''
        }. Access through staff roles is not affected. Add anyone who should stay to the team first.`}
        confirmText="Turn on strict access"
      />

      <ConfirmationModal
        isOpen={confirmForce}
        onClose={() => setConfirmForce(false)}
        onConfirm={() => {
          setConfirmForce(false);
          void runSync({ force: true }, 'force');
        }}
        title="Apply the held role removals?"
        message="These removals were held back by setup mode or by the safety cap on how many roles one pass may remove. Mr. Roboto removes them now. Check the preview first if you are not sure who they are."
        confirmText="Apply removals"
      />

      <ConfirmationModal
        isOpen={!!unlink}
        onClose={() => busy !== 'unlink' && setUnlink(null)}
        onConfirm={() => void doUnlink()}
        title={`Unlink ${channelName ? `#${channelName}` : 'the channel'}?`}
        message="Argus forgets that this channel belongs to the partner. The channel itself stays in Discord exactly as it is."
        confirmText="Unlink"
        isLoading={busy === 'unlink'}
      >
        <label className="flex items-start gap-2 text-sm text-glass-300">
          <input
            type="checkbox"
            className="form-checkbox mt-0.5"
            checked={!!unlink?.recreate}
            onChange={(e) => setUnlink({ recreate: e.target.checked })}
          />
          <span>
            Have Mr. Roboto create a new channel
            <span className="block text-xs text-glass-500">
              {unlink?.recreate
                ? 'Management stays on, and Mr. Roboto creates a fresh channel for the team on its next pass.'
                : 'Management is turned off too, so nothing changes in Discord until you link a channel or turn it back on.'}
            </span>
          </span>
        </label>
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => void revokePerson()}
        title={revokeTarget ? `Remove ${discordName(revokeTarget, revokeTarget.discord_id)}’s access?` : 'Remove access?'}
        message="They are not on the team. Mr. Roboto takes away their access to this channel. If they hold a partner role and are on no other partner team, that role is removed too."
        confirmText="Remove access"
        isLoading={!!revokeTarget && busy === `person:${revokeTarget.discord_id}`}
      />
    </div>
  );
};
